import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useApproveShopRequestMutation,
  useCreateShopMutation,
  useDeleteShopMutation,
  useGetShopRequestsQuery,
  useGetShopsPaginatedWithSearchQuery,
  useImportShopsMutation,
  useLazyCheckShopQuery,
  useLazyGetShopHistoryQuery,
  useRejectShopRequestMutation,
  useRequestShopPreferenceChangeMutation,
  useUpdateShopMutation,
} from "../../api/shopsApi";
import { Shop, ShopRequest, ShopAudit } from "../../types/shop";
import { useAppSelector } from "../../app/hooks";
import { AddButton } from "../../components/common/AddButton";
import PageTitle from "../../components/common/PageTitle";
import { StoreIcon } from "../../components/common/pageIcons";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";
import { useDataTablePagination } from "../../hooks/useDataTablePagination";
import PageLayout from "../../components/layout/PageLayout";
import DeleteConfirmationDialog from "./components/DeleteConfirmationDialog";
import { ImportShopsModal } from "./components/ImportShopsModal";
import ShopFormModal from "./components/ShopFormModal";
import ShopRequestsList from "./components/ShopRequestsList";
import ShopsList from "./components/ShopsList";

const ShopsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const authUser = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = authUser?.role === "SuperAdmin";
  const canCreateShop = ["Admin", "Dispatcher", "Fleet", "SuperAdmin"].includes(
    authUser?.role || ""
  );
  const canDeleteShop = ["SuperAdmin", "Admin", "Fleet", "Dispatcher"].includes(
    authUser?.role || ""
  );
  const canImportShops = [
    "SuperAdmin",
    "Admin",
    "Fleet",
    "Dispatcher",
  ].includes(authUser?.role || "");

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [feedback, setFeedback] = useState<{
    severity: "success" | "info" | "error";
    message: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<"shops" | "requests">(() =>
    searchParams.get("view") === "requests" ? "requests" : "shops",
  );
  const setShopViewMode = (next: "shops" | "requests") => {
    setViewMode(next);
    if (next === "requests") {
      setSearchParams({ view: "requests" }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };
  const [existingShopMatch, setExistingShopMatch] = useState<Shop | null>(null);
  const [pendingTargetPreference, setPendingTargetPreference] = useState<
    number | null
  >(null);
  const [pendingReason, setPendingReason] = useState<string>("");
  const [triggerHistory] = useLazyGetShopHistoryQuery();

  const {
    items: shops,
    isLoading,
    isLoadingRows,
    isLoadingMore,
    totalCount,
    hasNextPage,
    handleSearchChange,
    handleLoadMore,
    resetTableItems,
  } = useDataTablePagination<Shop>(
    useGetShopsPaginatedWithSearchQuery,
    DEFAULT_DATA_TABLE_PAGE_SIZE
  );
  const [createShop, { isLoading: isCreating }] = useCreateShopMutation();
  const [updateShop, { isLoading: isUpdating }] = useUpdateShopMutation();
  const [deleteShop, { isLoading: isDeleting }] = useDeleteShopMutation();
  const [importShops, { isLoading: isImporting }] = useImportShopsMutation();
  const [triggerCheckShop] = useLazyCheckShopQuery();
  const [requestPreferenceChange] = useRequestShopPreferenceChangeMutation();
  const { data: requests, isLoading: isLoadingRequests } =
    useGetShopRequestsQuery(
      isSuperAdmin
        ? { status: 0 }
        : { status: 0, requestedByUserId: authUser?.id }
    );
  const [approveRequest] = useApproveShopRequestMutation();
  const [rejectRequest] = useRejectShopRequestMutation();

  const pendingRequestCount = requests?.length ?? 0;
  const hasRequests = pendingRequestCount > 0;
  const [historyDialog, setHistoryDialog] = useState<{
    shopId: number;
    shopName?: string | null;
    items: ShopAudit[];
  } | null>(null);

  const handleCreateClick = () => {
    setSelectedShop(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = (shop: Shop) => {
    setSelectedShop(shop);
    setIsFormModalOpen(true);
  };

  const handleDeleteClick = (shop: Shop) => {
    setSelectedShop(shop);
    setIsDeleteModalOpen(true);
  };

  const handleFormClose = () => {
    setIsFormModalOpen(false);
    setSelectedShop(null);
  };

  const handleFormSubmit = async (
    values: Omit<Shop, "id" | "createdAt" | "updatedAt">
  ) => {
    try {
      if (selectedShop) {
        if (!isSuperAdmin) {
          setFeedback({
            severity: "error",
            message: "Only SuperAdmin can edit shops.",
          });
          return;
        }
        await updateShop({ id: selectedShop.id, ...values }).unwrap();
      } else {
        // Check for existing shop before creating
        const check = await triggerCheckShop({
          address: values.address,
          name: values.name,
        }).unwrap();
        if (check.exists && check.shop) {
          setExistingShopMatch(check.shop);
          if (check.shop.preference !== values.preference) {
            setPendingTargetPreference(values.preference);
            setPendingReason("");
            setFeedback({
              severity: "info",
              message: `Shop already exists as ${check.shop.preference === 0 ? "Trusted" : "DNU"
                }. You can request a preference change.`,
            });
          } else {
            setPendingTargetPreference(null);
            setPendingReason("");
            setFeedback({
              severity: "info",
              message: `Shop already exists as ${check.shop.preference === 0 ? "Trusted" : "DNU"
                }.`,
            });
          }
          return;
        }

        const result = await createShop(values).unwrap();
        if (result.requestCreated) {
          setFeedback({
            severity: "info",
            message: result.message || "Request created; an admin will review.",
          });
        } else {
          setFeedback({
            severity: "success",
            message: result.message || "Shop created.",
          });
        }
      }
      resetTableItems();
      handleFormClose();
    } catch (error) {
      console.error("Failed to save shop:", error);
      setFeedback({ severity: "error", message: "Failed to save shop." });
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedShop) {
      try {
        await deleteShop(selectedShop.id).unwrap();
        resetTableItems();
        setIsDeleteModalOpen(false);
        setSelectedShop(null);
      } catch (error) {
        console.error("Failed to delete shop:", error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedShop(null);
  };

  const handleImportClick = () => {
    setIsImportModalOpen(true);
  };

  const handleImport = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const result = await importShops(formData).unwrap();
    resetTableItems();
    return result;
  };

  const handleApproveRequest = async (request: ShopRequest) => {
    try {
      await approveRequest({ id: request.id }).unwrap();
      setFeedback({ severity: "success", message: "Request approved." });
    } catch (error) {
      console.error("Failed to approve request:", error);
      setFeedback({ severity: "error", message: "Failed to approve request." });
    }
  };

  const handleRejectRequest = async (request: ShopRequest) => {
    try {
      await rejectRequest({ id: request.id }).unwrap();
      setFeedback({ severity: "success", message: "Request rejected." });
    } catch (error) {
      console.error("Failed to reject request:", error);
      setFeedback({ severity: "error", message: "Failed to reject request." });
    }
  };

  const handleShowHistory = async (request: ShopRequest) => {
    try {
      const history = await triggerHistory(request.shopId).unwrap();
      const shopName = request.shopName ?? history[0]?.shopName;
      setHistoryDialog({ shopId: request.shopId, shopName, items: history });
    } catch (error) {
      console.error("Failed to load history:", error);
      setFeedback({ severity: "error", message: "Failed to load history." });
    }
  };

  const handleShowHistoryForShop = async (shop: Shop) => {
    try {
      const history = await triggerHistory(shop.id).unwrap();
      const shopName = shop.name || history[0]?.shopName;
      setHistoryDialog({ shopId: shop.id, shopName, items: history });
    } catch (error) {
      console.error("Failed to load history:", error);
      setFeedback({ severity: "error", message: "Failed to load history." });
    }
  };

  const handleRequestPreferenceChange = async () => {
    if (!existingShopMatch || pendingTargetPreference === null) return;
    try {
      await requestPreferenceChange({
        id: existingShopMatch.id,
        targetPreference: pendingTargetPreference,
        reason: pendingReason || undefined,
      }).unwrap();
      setFeedback({
        severity: "success",
        message: "Preference change request created.",
      });
      setExistingShopMatch(null);
      setPendingTargetPreference(null);
      setPendingReason("");
      handleFormClose();
    } catch (error) {
      console.error("Failed to request preference change:", error);
      setFeedback({
        severity: "error",
        message: "Failed to request preference change.",
      });
    }
  };

  return (
    <PageLayout>
      <Box>
        {feedback && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
            >
              {feedback.message}
            </Alert>
          </Box>
        )}
        <PageTitle
          title="Shops"
          icon={<StoreIcon />}
          titleRowSx={{ gap: 2 }}
          endAdornment={
            <>
              {isSuperAdmin && (
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, value) => value && setShopViewMode(value)}
                  size="small"
                >
                  <ToggleButton value="shops">Shops</ToggleButton>
                  <ToggleButton value="requests">
                    <Badge
                      color="error"
                      badgeContent={
                        pendingRequestCount > 0 ? pendingRequestCount : null
                      }
                    >
                      DNU Requests
                    </Badge>
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              {!isSuperAdmin && hasRequests && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setShopViewMode(
                      viewMode === "requests" ? "shops" : "requests",
                    )
                  }
                >
                  {viewMode === "requests"
                    ? "Back to Shops"
                    : `My Requests (${pendingRequestCount})`}
                </Button>
              )}
            </>
          }
          actions={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {viewMode === "shops" && (
                <>
                  {canImportShops && (
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={handleImportClick}
                    >
                      Import
                    </Button>
                  )}
                  {canCreateShop && (
                    <AddButton onClick={handleCreateClick}>Add Shop</AddButton>
                  )}
                </>
              )}
            </Box>
          }
        />

        {viewMode === "shops" && (
          <>
            <ShopsList
              shops={shops}
              isLoading={isLoading}
              onEditClick={isSuperAdmin ? handleEditClick : undefined}
              onDeleteClick={canDeleteShop ? handleDeleteClick : undefined}
              canEdit={isSuperAdmin}
              canDelete={canDeleteShop}
              canViewHistory={isSuperAdmin}
              onHistoryClick={
                isSuperAdmin ? handleShowHistoryForShop : undefined
              }
              totalCount={totalCount}
              hasNextPage={hasNextPage}
              isLoadingMore={isLoadingMore}
              isLoadingRows={isLoadingRows}
              onLoadMore={handleLoadMore}
              // Search functionality
              enableSearch={true}
              onSearchChange={handleSearchChange}
            />

            <ShopFormModal
              open={isFormModalOpen}
              onClose={handleFormClose}
              onSubmit={handleFormSubmit}
              initialValues={selectedShop}
              isLoading={isCreating || isUpdating}
            />

            <DeleteConfirmationDialog
              open={isDeleteModalOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              isLoading={isDeleting}
              shop={selectedShop}
            />

            <ImportShopsModal
              open={isImportModalOpen}
              onClose={() => setIsImportModalOpen(false)}
              onImport={handleImport}
              isLoading={isImporting}
            />
          </>
        )}

        {viewMode === "requests" && requests && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {isSuperAdmin ? "Pending DNU Requests" : "My DNU Requests"}
            </Typography>
            <ShopRequestsList
              requests={requests}
              isLoading={isLoadingRequests}
              title=""
              onApprove={isSuperAdmin ? handleApproveRequest : undefined}
              onReject={isSuperAdmin ? handleRejectRequest : undefined}
              onShowHistory={handleShowHistory}
            />
          </Box>
        )}

        <Dialog
          open={!!existingShopMatch}
          onClose={() => {
            setExistingShopMatch(null);
            setPendingTargetPreference(null);
            setPendingReason("");
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Shop Already Exists</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2, color: "text.primary" }}>
              {existingShopMatch &&
                pendingTargetPreference !== null &&
                existingShopMatch.preference !== pendingTargetPreference
                ? "We found a shop at this address. You can’t create a duplicate, but you can request a preference change below."
                : "We found a shop at this address with the same preference. This will be marked as a duplicate and cannot be added."}
            </Typography>
            {existingShopMatch && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing Shop
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      Name: {existingShopMatch.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      Address: {existingShopMatch.address}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      Preference:{" "}
                      {existingShopMatch.preference === 0 ? "Trusted" : "DNU"}
                    </Typography>
                  </Stack>
                </Grid>
                {pendingTargetPreference !== null &&
                  existingShopMatch.preference !== pendingTargetPreference && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Request Preference Change
                      </Typography>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          Target Preference:{" "}
                          {pendingTargetPreference === 0 ? "Trusted" : "DNU"}
                        </Typography>
                        <TextField
                          label="Reason"
                          multiline
                          minRows={3}
                          value={pendingReason}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPendingReason(e.target.value)
                          }
                          fullWidth
                          inputProps={{ maxLength: 500 }}
                          helperText={`${pendingReason.length}/500 characters`}
                        />
                      </Stack>
                    </Grid>
                  )}
              </Grid>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button
                onClick={() => {
                  setExistingShopMatch(null);
                  setPendingTargetPreference(null);
                  setPendingReason("");
                }}
              >
                Close
              </Button>
              {existingShopMatch &&
                pendingTargetPreference !== null &&
                existingShopMatch.preference !== pendingTargetPreference && (
                  <Button
                    variant="contained"
                    onClick={handleRequestPreferenceChange}
                  >
                    Request Change to{" "}
                    {pendingTargetPreference === 0 ? "Trusted" : "DNU"}
                  </Button>
                )}
            </Box>
          </DialogContent>
        </Dialog>
      </Box>

      <Dialog
        open={!!historyDialog}
        onClose={() => setHistoryDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Shop History{" "}
          {historyDialog
            ? historyDialog.shopName
              ? `${historyDialog.shopName} (#${historyDialog.shopId})`
              : `#${historyDialog.shopId}`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          {historyDialog?.items?.length ? (
            <Stack spacing={1.5}>
              {historyDialog.items.map((item) => {
                const color = item.action.toLowerCase().includes("approved")
                  ? "success.main"
                  : item.action.toLowerCase().includes("rejected")
                    ? "error.main"
                    : "info.main";
                return (
                  <Box
                    key={item.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: color,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {item.action}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {new Date(item.createdAt).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {item.details || "—"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Actor: {item.actorName ?? item.actorUserId ?? "Unknown"}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2">No history found.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default ShopsPage;
